import React, {useRef, useEffect, useState, ChangeEvent} from 'react'
import {Box, IconButton} from '@mui/material'
import {Close, FileUpload} from '@mui/icons-material'
import {toast} from 'react-toastify'

interface ImagePickerScrollerProps {
  value: string[]
  setImageUrls: (urls: string[]) => void
  maxVisible?: number
  maxFiles?: number
  maxFileSize?: number // in bytes
  onFilesChange?: (files: File[]) => void // Callback to expose files to parent
  disabled?: boolean // ADDED: New disabled prop
}

const IMAGE_SIZE = 90
const IMAGE_SPACING = 10

const ImagePickerScroller: React.FC<ImagePickerScrollerProps> = ({
  value,
  setImageUrls,
  maxVisible = 5,
  maxFiles = 10,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  onFilesChange,
  disabled = false, // ADDED: Default disabled to false
}) => {
  const [files, setFiles] = useState<File[]>([])
  const [urls, setUrls] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Generate and clean up preview URLs for new files
  useEffect(() => {
    // Clean up old URLs
    urls.forEach((url) => URL.revokeObjectURL(url))
    const newUrls = files.map((file) => URL.createObjectURL(file))
    setUrls(newUrls)
    
    // Do NOT call setImageUrls here! Only update parent when removing existing images.
    // Notify parent component of file changes
    if (onFilesChange) {
      onFilesChange(files)
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      newUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [files]) // Remove 'value' from dependencies to prevent infinite loop

  // Keep internal URLs in sync with value prop (if parent clears)
  useEffect(() => {
    if (value.length === 0 && files.length > 0) {
      setFiles([])
      setUrls([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const validateFile = (file: File): boolean => {
    // Check file size
    if (file.size > maxFileSize) {
      toast.error(`${file.name} is too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024))}MB`)
      return false
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    if (!validTypes.includes(file.type)) {
      toast.error(`${file.name} is not a valid image file`)
      return false
    }

    return true
  }

  const handleAddImages = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      
      // Check if adding these files would exceed the limit
      const totalImages = value.length + files.length + newFiles.length
      if (totalImages > maxFiles) {
        toast.error(`Maximum ${maxFiles} images allowed`)
        return
      }

      // Validate each file
      const validFiles = newFiles.filter(file => validateFile(file))
      
      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles])
      }
    }
  }

  const handleRemoveImage = (index: number) => {

    console.log('Removing image at index:', index, 'Total images:', allUrls.length)
    console.log('Value length:', value.length, 'URLs length:', urls.length)
    
    // Check if it's an existing image (from value prop) or a new file
    if (index < value.length) {
      // Remove existing image by updating the value prop
      const newValue = value.filter((_, i) => i !== index)
      console.log('Removing existing image, new value:', newValue)
      setImageUrls(newValue) // This will notify the parent component
    } else {
      // Remove new file
      const fileIndex = index - value.length
      console.log('Removing new file at fileIndex:', fileIndex)
      setFiles((prev) => prev.filter((_, i) => i !== fileIndex))
    }
  }

  const containerWidth = maxVisible * IMAGE_SIZE + (maxVisible - 1) * IMAGE_SPACING
  const allUrls = [...value, ...urls]

  return (
    <Box sx={{ marginBottom: '20px', width: '100%', marginLeft: '10px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Upload button on the left, styled as in New.tsx - MODIFIED: Disabled state styling */}
        <Box
          sx={{
            marginBottom: '10px',
            marginRight: '8px',
            position: 'relative',
            backgroundColor: allUrls.length === 0 ? 'grey' : 'transparent',
            width: `${IMAGE_SIZE}px`,
            height: `${IMAGE_SIZE}px`,
            cursor: disabled ? 'not-allowed' : 'pointer', // ADDED: Change cursor when disabled
            border: '2px dashed #ccc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            opacity: disabled ? 0.5 : 1, // ADDED: Reduce opacity when disabled
          }}
          onClick={() => {
            // ADDED: Don't allow click if disabled
            if (!disabled) {
              fileInputRef.current?.click()
            }
          }}
        >
          <div className='d-flex flex-column justify-content-center align-items-center'>
            <FileUpload style={{ fontSize: '24px', marginBottom: '4px', color: disabled ? '#999' : '#666' }} />
            <label htmlFor="" style={{fontSize: '14px', color: disabled ? '#999' : '#666', fontWeight: 'bold' }}>
              {disabled ? 'View Only' : 'Upload'} {/* ADDED: Change label when disabled */}
            </label>
          </div>
          <input
            type='file'
            accept='image/*'
            multiple
            ref={fileInputRef}
            onChange={handleAddImages}
            style={{ display: 'none' }}
            disabled={disabled} // ADDED: Disable input element
          />
        </Box>
        {/* Images to the right, fixed width with scroll, styled as in New.tsx */}
        <Box
          sx={{
            display: 'flex',
            width: `${containerWidth}px`,
            overflowX: allUrls.length > maxVisible ? 'auto' : 'hidden',
            overflowY: 'hidden',
            // Custom scrollbar
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#a8a8a8',
              },
            },
          }}
        >
          {allUrls.map((url, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: `${IMAGE_SIZE}px`,
                height: `${IMAGE_SIZE}px`,
                border: '1px solid #ddd',
                borderRadius: '4px',
                overflow: 'hidden',
                flexShrink: 0,
                marginRight: index !== allUrls.length - 1 ? '8px' : 0,
                marginBottom: '10px',
                opacity: disabled ? 0.8 : 1, // ADDED: Slightly reduce opacity when disabled
              }}
            >
              {/* MODIFIED: Only show close button if not disabled */}
              {!disabled && (
                <IconButton
                  size='small'
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                  onClick={() => handleRemoveImage(index)}
                >
                  <Close style={{ fontSize: '16px', color: 'grey' }} />
                </IconButton>
              )}
              <img
                src={url}
                height={IMAGE_SIZE}
                width={IMAGE_SIZE}
                alt={`Image ${index + 1}`}
                style={{ objectFit: 'cover' }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default ImagePickerScroller
